import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "@/app/navigation/routes";
import { useAuth } from "@/app/providers/useAuth";
import { Modal } from "@/shared/modal";
import { LogoutIcon } from "@/assets/svg";
import styles from "./SettingsLogoutModal.module.css";

export type SettingsLogoutModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
};

export const SettingsLogoutModal = ({ isOpen, onClose, onConfirm }: SettingsLogoutModalProps) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleConfirm = async () => {
    if (onConfirm) {
      onConfirm();
    } else {
      setIsLoggingOut(true);
      try {
        await logout();
      } finally {
        setIsLoggingOut(false);
        onClose();
        navigate(routes.login, { replace: true });
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={styles.modal}
      headerClassName={styles.header}
      bodyClassName={styles.body}
      title={
        <div className={styles.titleWrap}>
          <div className={styles.iconBadge}>
            <LogoutIcon className={styles.logoutIcon} aria-hidden="true" />
          </div>
          <div>
            <h2 className={styles.title}>Logout</h2>
            <p className={styles.subtitle}>Confirm session termination</p>
          </div>
        </div>
      }
    >
      <p className={styles.message}>
        Are you sure you want to logout? You will need to sign in again to access your account.
      </p>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={onClose}
          disabled={isLoggingOut}
        >
          Cancel
        </button>
        <button
          type="button"
          className={styles.confirmButton}
          onClick={handleConfirm}
          disabled={isLoggingOut}
        >
          {isLoggingOut ? "Logging out..." : "Yes, Logout"}
        </button>
      </div>
    </Modal>
  );
};
